/**
 * PaperClipDetector  —  3꼭지 "시험지 잘림" 탐지
 *
 * Checks whether any of the 4 paper corner markers have drifted to the edge
 * of (or outside) the camera frame. When a corner is clipped the homography
 * estimate degrades and the gaze-vs-paper zone classification becomes
 * unreliable. This module issues a severity-graded warning so the HUD can
 * inform the proctor before evidence quality drops.
 *
 * Design note: returns a confidence + narratorText in the same style as
 * PartialVisibilityClassifier (honest-unknown over noisy signal).
 */

export interface Point2D {
  x: number;
  y: number;
}

export type CornerName = 'TL' | 'TR' | 'BR' | 'BL';

export interface ClippedCorner {
  id: CornerName;
  corner: Point2D;
  /** Closest edge distance (0 = on boundary, negative = outside frame). */
  marginX: number;
  marginY: number;
  minMargin: number;
}

export interface PaperClipResult {
  /** True when at least one corner is within CLIP_THRESHOLD of the edge. */
  isClipped: boolean;
  /** Which corners are clipped (sorted by severity). */
  clippedCorners: ClippedCorner[];
  /**
   * Fraction of the paper's parallelogram area that is visible.
   * 1.0 = fully inside frame, 0.0 = completely outside.
   */
  visibleFraction: number;
  /** 'none' | 'minor' (1 corner borderline) | 'major' (2+ corners or any outside). */
  severity: 'none' | 'minor' | 'major';
  /** Korean narrator text (neutral wording). */
  narratorText: string;
  /** Matching PartialVisibilityClassifier color convention. */
  uiColor: 'green' | 'yellow' | 'orange' | 'red';
}

/** Corners within this margin of the edge are flagged 'minor'. */
const CLIP_MINOR_THRESHOLD = 0.06;
/** Corners within this margin (or outside) are flagged 'major'. */
const CLIP_MAJOR_THRESHOLD = 0.01;

const CORNER_NAMES: CornerName[] = ['TL', 'TR', 'BR', 'BL'];

/**
 * Detect paper clipping from 4 corner positions.
 *
 * @param corners  Normalized [0,1] corner positions in order TL, TR, BR, BL.
 *                 If fewer than 4 corners are provided, uses only those given.
 */
export function detectPaperClip(corners: Point2D[]): PaperClipResult {
  if (!corners || corners.length < 3) {
    return {
      isClipped: false,
      clippedCorners: [],
      visibleFraction: 1.0,
      severity: 'none',
      narratorText: '시험지 위치 미확인 (마커 3개 미만)',
      uiColor: 'red',
    };
  }

  const clipped: ClippedCorner[] = [];

  for (let i = 0; i < Math.min(corners.length, 4); i++) {
    const c = corners[i];
    const mx = Math.min(c.x, 1 - c.x);
    const my = Math.min(c.y, 1 - c.y);
    const minM = Math.min(mx, my);
    if (minM < CLIP_MINOR_THRESHOLD) {
      clipped.push({
        id: CORNER_NAMES[i],
        corner: c,
        marginX: mx,
        marginY: my,
        minMargin: minM,
      });
    }
  }

  clipped.sort((a, b) => a.minMargin - b.minMargin);

  // Count how many are truly outside (minMargin < 0) or very close.
  const outsideCount = clipped.filter(c => c.minMargin < 0).length;
  const nearEdgeCount = clipped.filter(c => c.minMargin < CLIP_MAJOR_THRESHOLD).length;
  const borderlineCount = clipped.length - nearEdgeCount;

  let severity: PaperClipResult['severity'];
  if (outsideCount > 0 || nearEdgeCount >= 2) severity = 'major';
  else if (nearEdgeCount === 1 || borderlineCount >= 1) severity = 'minor';
  else severity = 'none';

  const isClipped = severity !== 'none';

  // Estimate visible fraction from the paper's projected bounding box.
  const xs = corners.map(c => c.x);
  const ys = corners.map(c => c.y);
  const fullW = Math.max(...xs) - Math.min(...xs);
  const fullH = Math.max(...ys) - Math.min(...ys);
  const clipW = (Math.min(Math.max(...xs), 1) - Math.max(Math.min(...xs), 0));
  const clipH = (Math.min(Math.max(...ys), 1) - Math.max(Math.min(...ys), 0));
  const visibleFraction = (fullW > 0 && fullH > 0)
    ? Math.max(0, Math.min(1, (clipW * clipH) / (fullW * fullH)))
    : 1;

  const narratorText = buildNarrator(severity, clipped, visibleFraction);
  const uiColor: PaperClipResult['uiColor'] =
    severity === 'none' ? 'green'
    : severity === 'minor' ? 'yellow'
    : clipped.some(c => c.minMargin < 0) ? 'red'
    : 'orange';

  return { isClipped, clippedCorners: clipped, visibleFraction, severity, narratorText, uiColor };
}

function buildNarrator(
  severity: PaperClipResult['severity'],
  clipped: ClippedCorner[],
  visibleFraction: number,
): string {
  if (severity === 'none') return '시험지 4개 꼭짓점 모두 프레임 내 정상';
  const cornerList = clipped.map(c => c.id).join(', ');
  const pct = (visibleFraction * 100).toFixed(0);
  if (severity === 'minor') {
    return `시험지 일부 잘림: ${cornerList} 꼭짓점이 프레임 가장자리 근처 — 가시 면적 ${pct}%`;
  }
  return `시험지 잘림: ${cornerList} 꼭짓점 프레임 밖 또는 경계 — 가시 면적 ${pct}%, 홈그래피 신뢰도 저하`;
}